import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import CheckboxGroup from "@components/fields/CheckboxGroup";
import ExpertSelect from "@components/fields/ExpertSelect";
import FormButtons from "@components/fields/FormButtons";
import Form from "@components/forms/Form";
import {
  ExpertRoleCreateDocument,
  ExpertRoleCreateMutation,
  ExpertRoleCreateMutationVariables,
  ExpertRoleCreatePayload,
  ExpertRoleRoleEnum,
  GetExpertJourneysQuery,
  GetJourneyWithExpertQuery
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { getCareProfileName } from "@utils/modelUtils";

type Journey =
  | NonNullable<NonNullable<NonNullable<GetExpertJourneysQuery["expertJourneys"]["edges"]>[number]>["node"]>
  | GetJourneyWithExpertQuery["journey"];

type FormData = {
  expertId: string;
  journeyIds: string[];
};

type Props = {
  journey: Journey;
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
  refetch?: () => void;
};

const ExpertAssignmentForm = ({ journey, onCancel, onError, onSuccess, refetch }: Props) => {
  const defaultValues: FormData = {
    expertId: "",
    journeyIds: journey.owner.roles.map(role => role.journey.id)
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createExpertRole] = useMutation<ExpertRoleCreateMutation, ExpertRoleCreateMutationVariables>(
    ExpertRoleCreateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      expertId: yup.string().required(intl.formatMessage({ id: "errors.eligibility.expertId.blank" })),
      journeyIds: yup
        .array()
        .defined()
        .of(yup.string().defined())
        .min(1, intl.formatMessage({ id: "errors.eligibility.journeyIds.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const journeyOptions = useMemo(
    () =>
      journey.owner.roles.map(role => ({
        label: getCareProfileName({ intl, journey: role.journey }),
        value: role.journey.id
      })),
    [intl, journey.owner.roles]
  );

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return Promise.all(
      form.getValues().journeyIds.map(async journeyId =>
        createExpertRole({
          variables: {
            expertId: form.getValues().expertId,
            journeyId,
            role: ExpertRoleRoleEnum.PRIMARY
          }
        })
      )
    ).then(response => {
      const expertRoles = response.map(expertRole => expertRole.data?.expertRoleCreate) as ExpertRoleCreatePayload[];
      handleMutationResponse<FormData>({
        data: expertRoles,
        intl,
        onError,
        onSuccess: () => {
          refetch?.();
          onSuccess();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.expertAssignment"
      });
    });
  }, [createExpertRole, form, intl, onError, onSuccess, refetch]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className={journey.owner.roles.length > 1 ? "" : "ion-hide"}>
        <div>
          <FormattedMessage id="forms.expertAssignment.multipleJourneys" />
        </div>
        <CheckboxGroup<FormData>
          control={form.control}
          //@ts-expect-error temperary fix, the type is an array but the error that gets passed in is an object
          errors={form.formState.errors.journeyIds ? [form.formState.errors.journeyIds] : []}
          label={intl.formatMessage({ id: "forms.expertAssignment.journeyIdsLabel" })}
          name="journeyIds"
          options={journeyOptions}
          required
        />
      </div>
      <div>
        <FormattedMessage id="forms.expertAssignment.client" />
        <div className="bold">{journey.owner?.fullName}</div>
      </div>
      <br />
      <div>
        <FormattedMessage id="forms.expertAssignment.lovedOne" />
        <div className="bold">
          {journey.owner.roles.map(role => getCareProfileName({ intl, journey: role.journey }))}
        </div>
      </div>
      <br />
      <div>
        <FormattedMessage id="forms.expertAssignment.currentExpert" />
        <div className="bold">{journey.primaryExpert?.fullName}</div>
      </div>
      <ExpertSelect<FormData>
        allowAny={false}
        control={form.control}
        error={form.formState.errors.expertId}
        initialOption={{ label: undefined, value: null }}
        label={intl.formatMessage({ id: "forms.expertAssignment.expertIdLabel" })}
        name="expertId"
      />
      <FormButtons hookForm={form} onCancel={onCancel} saveKey="forms.expertAssignment.reassign" />
    </Form>
  );
};

export default ExpertAssignmentForm;
